import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  onTextInput: (text: string) => void;
  isLoading?: boolean;
  className?: string;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  onTextInput,
  isLoading = false,
  className = ''
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState('');
  const [inputMode, setInputMode] = useState<'file' | 'text'>('file');
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv',
      'application/csv'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      return 'Format de fichier non supporté. Utilisez Excel (.xlsx, .xls) ou CSV.';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'Fichier trop volumineux. Taille maximum : 10MB.';
    }

    return null;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const validationError = validateFile(file);
      
      if (validationError) {
        setError(validationError);
        return;
      }

      setSelectedFile(file);
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: isLoading
  });

  const handleTextSubmit = () => {
    if (textInput.trim()) {
      onTextInput(textInput.trim());
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const clearText = () => {
    setTextInput('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Mode Toggle */}
      <div className="flex items-center space-x-2 mb-4">
        <button
          onClick={() => setInputMode('file')}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            inputMode === 'file' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📁 Fichier
        </button>
        <button
          onClick={() => setInputMode('text')}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            inputMode === 'text' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📝 Texte
        </button>
      </div>

      {inputMode === 'file' ? (
        <div className="space-y-4">
          {/* File Upload Zone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
              ${isDragActive 
                ? 'border-blue-400 bg-blue-50 scale-105' 
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              ${selectedFile ? 'border-green-400 bg-green-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {isLoading ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-blue-600 font-medium">Analyse en cours...</p>
              </div>
            ) : selectedFile ? (
              <div className="flex flex-col items-center space-y-3">
                <CheckCircle className="w-12 h-12 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">{selectedFile.name}</p>
                  <p className="text-sm text-green-700">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFile();
                  }}
                  className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Supprimer</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-3">
                <Upload className="w-12 h-12 text-gray-400" />
                <div>
                  <p className="text-gray-600 font-medium">
                    {isDragActive 
                      ? 'Déposez votre fichier ici...' 
                      : 'Glissez-déposez votre business plan'
                    }
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    ou <span className="text-blue-600 font-medium">cliquez pour sélectionner</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Formats supportés : Excel (.xlsx, .xls), CSV • Max 10MB
                  </p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Text Input */}
          <div>
            <label htmlFor="business-plan-text" className="block text-sm font-medium text-gray-700 mb-2">
              Collez vos données financières
            </label>
            <textarea
              id="business-plan-text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={8}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Collez ici vos données financières : revenus mensuels, charges fixes, charges variables, investissements, etc."
            />
            <p className="text-xs text-gray-500 mt-1">
              {textInput.length} caractères • Incluez les montants, périodes et descriptions
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleTextSubmit}
              disabled={!textInput.trim() || isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Utiliser ce texte
            </button>
            {textInput && (
              <button
                onClick={clearText}
                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Effacer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};